import { NOT_FOUND, pathToAction } from 'redux-first-router'

import deepEqual from 'deep-equal'
import { ApplicationRoutes, PlaceItem, PlaceStack, Route, RoutePayload } from 'navigation'

/**
 * Represents the the current navigation stack, and future navigation possibilities (primary, secondary,
 * tertiary navigation options).
 *
 * Note that navigation in this app somewhat resembles an android app activity-stack in the sense
 * that some pages are under/above others rather than before/after, so you can go back,forward,up
 * and down.
 */

export const createPlaceStack = (route: Route, routes: ApplicationRoutes, params: RoutePayload = {} as any): PlaceStack => {
  const current: PlaceItem[] = [
    {
      type: route.type,
      modal: route.modal || params.modal,
      next: route.next,
      prev: route.prev,
      payload: params
    }
  ]
  let temp = route
  while (temp && temp.prev) {
    let _temp = routes[temp.prev]
    if (!_temp) {
      throw new Error(
        `expected to find a route called ${temp.prev}, but it isnt there`
      )
    } else {
      temp = _temp
    }
    current.unshift({
      type: temp.type,
      modal: temp.modal,
      next: temp.next,
      prev: temp.prev,
      payload: params
    })
  }
  if (route.ancestry && route.ancestry.length) {
    return createPlaceStack(
      routes[route.ancestry[route.ancestry.length - 1]],
      routes,
      params
    ).concat([current])
  } else {
    return [current]
  }
}

export const getActiveRouteName = (stack: PlaceStack, route_names: string[]): string => {
  let active_route_name = null
  for (let i = stack.length - 1; i >= 0; i--) {
    let level = stack[i]
    for (let j = level.length - 1; j >= 0; j--) {
      let place = level[j]
      active_route_name = route_names.find(route => route === place.type)
      if (active_route_name) return active_route_name
    }
  }
}

export interface NavigationRoute {
  type: string
  icon: string
  payload: RoutePayload
  large_screen: boolean
  active: boolean
}
export interface Navigation {
  active_route_name: string
  routes: NavigationRoute[]
}

export const createNav = (stack: PlaceStack, routes: ApplicationRoutes, route_names: string[] = [], params: RoutePayload = {} as any): Navigation => {
  let active_route_name = getActiveRouteName(stack, route_names)
  let nav = route_names.reduce((r, p) => {
    let route = routes[p]
    if (route) {
      r[p] = {
        type: route.type,
        icon: route.icon,
        payload: {
          ...params
        },
        large_screen: route.large_screen,
        active: active_route_name === route.type
      }
    }
    return r
  }, {})
  return {
    active_route_name,
    routes: Object.values(nav)
  }
}

export const createPrimaryNav = (stack, routes, primaries = [], params) => {
  return createNav(stack, routes, primaries, {})
}

export const createSecondaryNav = (stack, routes, secondaries = [], params) => {
  return createNav(stack, routes, secondaries, params)
}

export const createTertiaryNav = (stack, routes, tertiaries = [], params) => {
  return createNav(stack, routes, tertiaries, params)
}

export const getIndexInStack = (route, stack) => {
  for (let i = stack.length - 1; i >= 0; i--) {
    let r = stack[i][0]
    if (r.type === route.type && deepEqual(r.payload, route.payload)) {
      return i
    }
  }
  return -1
}

export const findTheLastModal = (stack: PlaceStack): number => {
  return walkTheStackDown(stack, p => p.modal)
}

export const walkTheStackDown = (stack: PlaceStack, f: (s: PlaceItem) => boolean): number => {
  for (let i = stack.length - 1; i >= 0; i--) {
    if (f(stack[i][0])) return i
  }
  return -1
}

export const sameOrSiblingModal = (
  { stack: oldStack, modal: oldModal },
  { stack: newStack, modal: newModal }
) => {
  return oldStack[oldModal][0].type === newStack[newModal][0].type
}

export const isRedirect = ({ meta: { location: { kind } = {} } = {} } = {}) => {
  return kind && kind === 'redirect'
}

export interface RoutesSetup {
  routes: ApplicationRoutes
  nav_hierarchy
}

export interface InitOptions {
  routes: RoutesSetup
  path: string
}


const init = ({ routes: { routes, nav_hierarchy }, path = '/' }: InitOptions) => {
  // the initial route should typically be one of our defined routes, unless
  // someone navigates to a non-existent route in which case we'll fabricate
  // a route and let downstream code handle the problem of telling the user
  let initialAction = pathToAction(path, routes)
  let initialRoute = (initialAction && routes[initialAction.type]) || routes[NOT_FOUND]

  // console.log(JSON.stringify(initialAction, null, 2))
  // console.log(JSON.stringify(initialRoute, null, 2))
  // create the initial stack based on the requested path (server-side-render)
  // so that things like "up" navigation work correctly if we're landing on
  // a modal screen.
  let initialStack = createPlaceStack(
    initialRoute,
    routes,
    initialAction.payload as any
  )

  let {
    active_route_name: active_primary_name,
    routes: initialPrimary
  } = createPrimaryNav(
    initialStack,
    routes,
    nav_hierarchy.primary,
    initialRoute.payload
  )
  let active_modal = findTheLastModal(initialStack)
  if (active_modal >= 0) {
    active_modal = initialStack[active_modal][0] // first on the level is the modal root
  }

  let {
    active_route_name: active_secondary_name,
    routes: initialSecondary
  } = createSecondaryNav(
    initialStack,
    routes,
    nav_hierarchy.secondary[
    (active_modal && active_modal.type) || active_primary_name
    ],
    initialRoute.payload
  )

  let initialTertiary = createTertiaryNav(
    initialStack,
    routes,
    nav_hierarchy.tertiary[active_secondary_name],
    initialRoute.payload
  ).routes

  const navigation = function (
    state = {
      hierarchy: nav_hierarchy,
      stack: initialStack,
      primary: initialPrimary,
      secondary: initialSecondary,
      tertiary: initialTertiary
    },
    action = {}
  ) {
    if (
      // routing action, ie., redux-first-router style
      action &&
      action.meta &&
      action.meta.location &&
      action.meta.location.current &&
      action.type !== NOT_FOUND
    ) {
      const params = action.meta.location.current.payload
      const route = routes[action.type]
      if (route) {
        state.prev = {
          stack: state.stack,
          primary: state.primary,
          secondary: state.secondary,
          tertiary: state.tertiary
        }
        let index = getIndexInStack(
          { type: route.type, payload: params },
          state.stack
        )
        if (index >= 0) {
          // pop stack back to already present version of route
          state.stack = state.stack.slice(0, index + 1)
          state.stack[index] = [state.stack[index][0]]
        } else {
          let stack = createPlaceStack(route, routes, params)
          if (action.meta.rebuild_stack) {
            state.stack = stack
          } else {
            let indexOfModal = findTheLastModal(stack)
            if (state.stack.length && indexOfModal >= 0) {
              let indexOfOldModal = findTheLastModal(state.stack)
              if (
                action.meta.push !== true &&
                indexOfOldModal >= 0 &&
                (isRedirect(action) ||
                  sameOrSiblingModal(
                    { stack: state.stack, modal: indexOfOldModal },
                    { stack, modal: indexOfModal }
                  ))
              ) {
                state.stack = state.stack.slice(0, indexOfOldModal)
                state.stack = state.stack.concat(stack.slice(indexOfModal))
              } else {
                state.stack = state.stack.slice(0)
                state.stack = state.stack.concat(stack.slice(indexOfModal))
              }
            } else {
              state.stack = stack
            }
          }
        }
        let {
          active_route_name: active_primary_name,
          routes: primary
        } = createPrimaryNav(state.stack, routes, nav_hierarchy.primary, params)
        active_modal = findTheLastModal(state.stack)
        if (active_modal >= 0) {
          active_modal = state.stack[active_modal][0] // first on the level is the modal root
        }
        let {
          active_route_name: active_secondary_name,
          routes: secondary
        } = createSecondaryNav(
          state.stack,
          routes,
          nav_hierarchy.secondary[
          (active_modal && active_modal.type) || active_primary_name
          ],
          params
        )

        state.primary = primary
        state.secondary = secondary
        state.tertiary = createTertiaryNav(
          state.stack,
          routes,
          nav_hierarchy.tertiary[active_secondary_name],
          params
        ).routes
      }
    }
    return state
  }

  return navigation
}

export default init
